import { isLiveRequest } from '@platform/domain';
import { Check, CircleDashed, FilePenLine, Flag, ScanSearch, Stamp, X } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import type {
  DocumentStatus,
  EtaEnvironment,
  EtaStatus,
  EtaStatusDetail,
  Readiness,
} from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { cx } from '../../../ui/cx';
import styles from '../Sales.module.css';
import { MockLabel } from '../status';

type StageState = 'done' | 'current' | 'ended' | 'todo';

interface Stage {
  key: 'drafting' | 'finalIssue' | 'etaValidation' | 'result';
  icon: ReactNode;
  state: StageState;
  text: string;
  mock?: boolean;
}

const RESULTS: ReadonlySet<string> = new Set([
  'valid',
  'invalid',
  'rejected',
  'not_accepted',
  'cancelled',
]);

/**
 * "Document journey" (BLOCK-1-REDESIGN §A.5, DESIGN_SYSTEM §6): the four stages of a sales
 * document, the current one marked with aria-current="step". Every stage shows an icon, a label
 * and a state text, so no state is told by colour alone. A deleted draft ends the journey at its
 * first stage. Horizontal from 40rem, vertical below.
 */
export function JourneyTracker({
  status,
  readiness,
  etaStatus,
  environment,
  issuedAt,
  eta,
}: {
  status: DocumentStatus;
  readiness: Readiness | null;
  etaStatus: EtaStatus | null;
  environment: EtaEnvironment | null | undefined;
  issuedAt: string | null;
  /** Block 11: a request at the Tax Authority and the other side's answer show in stage 4. */
  eta?: EtaStatusDetail | null;
}) {
  const { t, formatDateTime } = useI18n();
  const titleId = useId();
  const j = t.detail.journey;
  const issued = status !== 'draft' && status !== 'discarded';
  const hasResult = Boolean(etaStatus && RESULTS.has(etaStatus));
  const isMock = Boolean(environment && environment !== 'production');
  // RI-08: a deleted draft ended its journey where it was, so stages 2–4 are not applicable — not
  // "not reached yet", which reads as still pending.
  const notReachedHere = status === 'discarded' ? j.notApplicable : j.notReached;

  const drafting: Stage = {
    key: 'drafting',
    icon: <FilePenLine size={18} aria-hidden="true" />,
    state: status === 'discarded' ? 'ended' : issued ? 'done' : 'current',
    text:
      status === 'discarded'
        ? t.docStatus.discarded
        : issued
          ? t.dashboard.stepDone
          : t.taxStatus[readiness ?? 'incomplete'],
  };
  const finalIssue: Stage = {
    key: 'finalIssue',
    icon: <Stamp size={18} aria-hidden="true" />,
    state: issued ? 'done' : 'todo',
    text:
      issued && issuedAt
        ? formatDateTime(issuedAt)
        : issued
          ? t.dashboard.stepDone
          : notReachedHere,
  };
  const validation: Stage = {
    key: 'etaValidation',
    icon: <ScanSearch size={18} aria-hidden="true" />,
    state: !issued ? 'todo' : hasResult ? 'done' : 'current',
    text: !issued
      ? notReachedHere
      : hasResult
        ? t.dashboard.stepDone
        : t.taxStatus[
            etaStatus ?? (status === 'signed' || status === 'signing' ? status : 'not_submitted')
          ],
    mock: isMock,
  };
  // Block 11 (document-cancellation §6): stage 4 shows a request in progress, the other side's
  // decline, and the final state. Liveness is the ONE predicate in @platform/domain (H-5, D-13):
  // `isLiveRequest` is in flight, or taken but not confirmed — the former inline copy, exactly.
  const live = (action: string) =>
    (eta?.requests ?? []).some((r) => r.action === action && isLiveRequest(r));
  const resultText = !(hasResult && etaStatus)
    ? notReachedHere
    : live('cancel') && etaStatus !== 'cancelled'
      ? j.cancelInProgress
      : live('decline_rejection') && etaStatus === 'rejected'
        ? j.declineInProgress
        : etaStatus === 'valid' && eta?.pending?.declineCancelRequestDate
          ? j.cancelDeclined
          : etaStatus === 'valid' && eta?.pending?.declineRejectRequestDate
            ? j.rejectionDeclined
            : t.taxStatus[etaStatus];
  const result: Stage = {
    key: 'result',
    icon: <Flag size={18} aria-hidden="true" />,
    state: hasResult ? 'current' : 'todo',
    text: resultText,
    mock: isMock,
  };
  const stages = [drafting, finalIssue, validation, result];

  return (
    <section className={styles.journey} aria-labelledby={titleId} data-testid="journey">
      <h2 id={titleId} className={styles.journeyTitle}>
        {j.label}
      </h2>
      <ol className={styles.journeyList} aria-labelledby={titleId}>
        {stages.map((stage, index) => {
          const isCurrent = stage.state === 'current' || stage.state === 'ended';
          return (
            <li
              key={stage.key}
              className={cx(styles.journeyStage, styles[`stage_${stage.state}`])}
              aria-current={isCurrent ? 'step' : undefined}
              data-stage={stage.key}
              data-state={stage.state}
            >
              <span className={styles.journeyMarker} aria-hidden="true">
                {stage.state === 'done' ? (
                  <Check size={16} strokeWidth={3} />
                ) : stage.state === 'ended' ? (
                  <X size={16} strokeWidth={3} />
                ) : stage.state === 'todo' ? (
                  <CircleDashed size={16} />
                ) : (
                  stage.icon
                )}
              </span>
              <span className={styles.journeyText}>
                <span className={styles.journeyLabel}>
                  <span className="visually-hidden">{`${index + 1}. `}</span>
                  {j[stage.key]}
                </span>
                <span className={styles.journeyState}>
                  {stage.text}
                  {stage.mock ? <MockLabel environment={environment} /> : null}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
